import { Copy, KeyRound, Plus, RefreshCw, ShieldX } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
  createIntegrationToken,
  loadIntegrationTokens,
  revokeIntegrationToken,
  updateIntegrationToken,
  type IntegrationToken,
  type IntegrationTokensSnapshot,
} from './integrations-api';
import './owner-pages.css';

function when(value: string | null): string {
  if (!value) return 'Never';
  const date = new Date(value);
  return Number.isFinite(date.getTime()) ? date.toLocaleString('en-GB') : '—';
}

export function IntegrationsPage() {
  const [snapshot, setSnapshot] = useState<IntegrationTokensSnapshot | null>(null);
  const [name, setName] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>([]);
  const [secret, setSecret] = useState<{ value: string; warning: string } | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    try {
      setSnapshot(await loadIntegrationTokens());
      setMessage('');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Integrations could not be loaded.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  async function create() {
    if (!name.trim() || selectedScopes.length === 0) return;
    setBusy(true);
    setMessage('');
    try {
      const result = await createIntegrationToken(name.trim(), selectedScopes);
      setSecret({ value: result.secret, warning: result.warning });
      setName('');
      setSelectedScopes([]);
      await refresh();
      setMessage('Credential created. Copy the token now; it will not be shown again.');
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Credential creation failed.');
    } finally {
      setBusy(false);
    }
  }

  async function copySecret() {
    if (!secret) return;
    try {
      await navigator.clipboard.writeText(secret.value);
      setMessage('API token copied to the clipboard.');
    } catch {
      setMessage('The API token could not be copied. Select the token above and copy it manually before dismissing it.');
    }
  }

  async function update(token: IntegrationToken, changes: { scopes?: string[]; enabled?: boolean }) {
    setBusy(true);
    setMessage('');
    try {
      await updateIntegrationToken(token.id, changes);
      await refresh();
      setMessage(`${token.name} updated.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Credential update failed.');
    } finally {
      setBusy(false);
    }
  }

  async function revoke(token: IntegrationToken) {
    if (!window.confirm(`Revoke ${token.name}? This cannot be undone.`)) return;
    setBusy(true);
    setMessage('');
    try {
      await revokeIntegrationToken(token.id);
      await refresh();
      setMessage(`${token.name} revoked.`);
    } catch (error) {
      setMessage(error instanceof Error ? error.message : 'Credential revocation failed.');
    } finally {
      setBusy(false);
    }
  }

  function toggleScope(scope: string) {
    setSelectedScopes((current) => current.includes(scope) ? current.filter((item) => item !== scope) : [...current, scope]);
  }

  if (loading) return <div className="ffs-next-loading">Loading Integrations…</div>;

  if (!snapshot) {
    return (
      <div className="ffs-workspace-page ffs-owner-page">
        <div className="ffs-workspace-message" aria-live="polite">{message || 'Integrations could not be loaded.'}</div>
        <section className="ffs-workspace-hero">
          <div><small>OWNER ONLY</small><h2>Integrations</h2><p>Credential data is unavailable. No integration state is being inferred.</p></div>
          <button type="button" className="ffs-button secondary" onClick={() => void refresh()}><RefreshCw size={14} /> Retry</button>
        </section>
      </div>
    );
  }

  return (
    <div className="ffs-workspace-page ffs-owner-page">
      {message ? <div className="ffs-workspace-message" aria-live="polite">{message}</div> : null}

      <section className="ffs-workspace-hero">
        <div>
          <small>OWNER ONLY</small>
          <h2>Integrations</h2>
          <p>Create scoped API credentials for trusted tools without exposing dashboard credentials.</p>
        </div>
        <button className="ffs-button secondary" type="button" disabled={busy} onClick={() => void refresh()}>
          <RefreshCw size={14} /> Refresh
        </button>
      </section>

      {secret ? (
        <section className="ffs-owner-secret">
          <strong>Copy this token now</strong>
          <p>{secret.warning}</p>
          <code>{secret.value}</code>
          <button type="button" className="ffs-button primary" onClick={() => void copySecret()}>
            <Copy size={14} /> Copy Token
          </button>
          <button type="button" className="ffs-button secondary" onClick={() => setSecret(null)}>Dismiss</button>
        </section>
      ) : null}

      <section className="ffs-workspace-card">
        <header><div><small>NEW CREDENTIAL</small><h3>Create API token</h3></div><KeyRound size={18} /></header>
        <div className="ffs-owner-create">
          <label><span>Name</span><input value={name} maxLength={80} placeholder="e.g. Touch Portal" onChange={(event) => setName(event.target.value)} /></label>
          <div className="ffs-scope-grid">
            {snapshot.scopes.map((scope) => (
              <label key={scope}>
                <input type="checkbox" checked={selectedScopes.includes(scope)} onChange={() => toggleScope(scope)} />
                <code>{scope}</code>
              </label>
            ))}
          </div>
          <button type="button" className="ffs-button primary" disabled={busy || !name.trim() || selectedScopes.length === 0} onClick={() => void create()}>
            <Plus size={14} /> Create Credential
          </button>
        </div>
      </section>

      <section className="ffs-workspace-card">
        <header>
          <div><small>AUTHORISED CLIENTS</small><h3>API credentials</h3></div>
          <span>{snapshot.tokens.filter((token) => !token.revokedAt).length}</span>
        </header>
        <div className="ffs-token-list">
          {snapshot.tokens.length === 0 ? (
            <div className="ffs-empty-state">No API credentials have been created.</div>
          ) : snapshot.tokens.map((token) => (
            <article key={token.id} className={token.revokedAt ? 'revoked' : ''}>
              <div className="title">
                <div><strong>{token.name}</strong><code>{token.tokenPrefix}…</code></div>
                <span>{token.revokedAt ? 'REVOKED' : token.enabled ? 'ENABLED' : 'DISABLED'}</span>
              </div>
              <div className="meta">
                <span>Created {when(token.createdAt)}</span>
                <span>Last used {when(token.lastUsedAt)}</span>
                <span>IP {token.lastUsedIp ?? '—'}</span>
              </div>
              <div className="ffs-scope-grid">
                {snapshot.scopes.map((scope) => {
                  const checked = token.scopes.includes(scope);
                  return (
                    <label key={scope}>
                      <input
                        type="checkbox"
                        checked={checked}
                        disabled={busy || Boolean(token.revokedAt) || (checked && token.scopes.length === 1)}
                        onChange={() => void update(token, { scopes: checked ? token.scopes.filter((item) => item !== scope) : [...token.scopes, scope] })}
                      />
                      <code>{scope}</code>
                    </label>
                  );
                })}
              </div>
              <div className="ffs-inline-actions">
                <button type="button" className="ffs-button secondary" disabled={busy || Boolean(token.revokedAt)} onClick={() => void update(token, { enabled: !token.enabled })}>
                  {token.enabled ? 'Disable' : 'Enable'}
                </button>
                <button type="button" className="ffs-button danger" disabled={busy || Boolean(token.revokedAt)} onClick={() => void revoke(token)}>
                  <ShieldX size={14} /> Revoke
                </button>
              </div>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
}
